import type { ServicePageData } from './types';

const gazebos: ServicePageData = {
  slug: 'gazebos',
  eyebrow: 'Signage',
  title: 'Turn shelter into a',
  titleAccent: 'branded marketing space.',
  intro:
    'If you have booked an outdoor market stall, a gazebo is usually a requirement of your set-up, so you might as well recover some cost and make it work for you. We take the guesswork out of choosing one, with two tried and tested brands to suit your budget.',
  heroImages: [
    '/home/gazebos/barkavenue-gazebo.jpg',
    '/home/gazebos/barkavenue-gazebo-2.jpg',
    '/home/gazebos/copper-beech.jpg',
    '/home/gazebos/curry-king-gazebo.jpg',
    '/home/gazebos/curry-king-gazebo-2.jpg',
    '/home/gazebos/heavy-3x3m.jpg',
    '/home/gazebos/heavy-3x4-5m.jpg',
    '/home/gazebos/heavy-3x6m.jpg',
    '/home/gazebos/heavy-frame.jpg',
    '/home/gazebos/heavy-frame-2.jpg',
    '/home/gazebos/standard-3x3m.jpg',
    '/home/gazebos/standard-3x4-5m.jpg',
    '/home/gazebos/standard-3x6m.jpg',
    '/home/gazebos/standard-feet.jpg',
    '/home/gazebos/wildjac-gazebo.jpg',
    '/home/gazebos/wildjac-gazebo-2.jpg',
    '/home/gazebos/mayfield-gazebo.jpg',
    '/home/gazebos/nightstop-gazebo.jpg',
    '/home/gazebos/barkavenue-gazebo.jpg',
    '/home/gazebos/curry-king-gazebo.jpg',
    '/home/gazebos/wildjac-gazebo.jpg',
  ],

  useCases: [
    { title: 'Outdoor markets and stalls', text: 'A gazebo is usually part of the set-up anyway, so make it work for your brand.' },
    { title: 'Exhibitions and trade shows', text: 'Stand out from the neighbouring stands with a branded canopy.' },
    { title: 'Building sites and trade events', text: 'Heavy-duty frames built for regular exposure to the elements.' },
    { title: 'Pop-up retail and hospitality', text: 'Branded stalls, food pop-ups and promotional activations, anywhere outdoors.' },
    { title: 'Festivals and corporate events', text: 'Custom canopies and walls that travel well and go up fast.' },
  ],

  optionsHeading: 'Select your gazebo',
  optionsIntro:
    'Choose one of our tried and tested gazebos, or supply your own frame for a canopy or wall refresh.',
  options: [
    {
      id: 'mid-range',
      title: 'Mid-range',
      lead: 'An excellent choice for light commercial, exhibitions and outdoor markets.',
      body: 'The options online are endless, so we have selected the brand we believe reflects our quality and suits most budgets. Even our standard gazebo is not a "budget" style.',
      image: '/home/gazebos/standard-3x3m.jpg',
      specs: [
        { label: 'Size', value: '3 x 3m, 3 x 4.5m or 3 x 6m frame with canopy.' },
        { label: 'Canopy', value: '100% waterproof 420D polyester PVC-backed fabric in 10 colours: black, pink, tan, blue, red, grey, brown, white and purple.' },
        { label: 'Frame', value: 'Powder-coated steel, 32 x 32mm box-section legs, nylon plastic joints, 3 height settings from 1400 to 2100mm.' },
        { label: 'Includes', value: 'Free carry bag, guy ropes and stakes. 1-year manufacturer warranty, spare parts available.' },
      ],
    },
    {
      id: 'heavy-duty',
      title: 'Heavy-duty',
      lead: 'Ideal for commercial customers who need a stronger frame for regular outdoor use.',
      body: 'Built for events where the gazebo will be up in all weathers. The hexagonal aluminium frame is noticeably stronger than a standard square-leg design.',
      image: '/home/gazebos/heavy-3x3m.jpg',
      specs: [
        { label: 'Size', value: '3 x 3m, 3 x 4.5m or 3 x 6m frame with canopy.' },
        { label: 'Canopy', value: '100% waterproof 600D polyester PVC-backed fabric in 14 colours, including two-tone options.' },
        { label: 'Frame', value: '6061/T6 aluminium, 40 x 40mm hexagonal legs, carbon-fibre reinforced joints, 5 height settings from 1400 to 2100mm.' },
        { label: 'Includes', value: 'Free carry bag, guy ropes and stakes. 2-year manufacturer warranty, spare parts available.' },
      ],
    },
    {
      id: 'wall-sets',
      title: 'Wall sets',
      lead: 'Enclose your gazebo to keep the weather and the wind out.',
      body: 'Each set is one solid plain panel, two with windows and one with a twin zipped doorway, sized to match your frame. A heavy-duty wheeled carry case is available for both ranges, subject to stock.',
      image: '/home/gazebos/heavy-frame-2.jpg',
      specs: [
        { label: 'Sizes', value: 'Matched to 3 x 3m, 3 x 4.5m or 3 x 6m frames.' },
        { label: 'Set contents', value: '1 solid panel, 2 with windows, 1 with a twin zipped doorway.' },
        { label: 'Also available', value: 'Half walls and a wheeled carry case for the full set-up.' },
      ],
    },
    {
      id: 'roof-valance-panels',
      title: 'Roof and valance panels',
      lead: 'Build eye-catching branding a piece at a time, without paying for an all-over print.',
      body: 'All-over printed gazebos look good but are expensive for an occasional event or a limited budget. Mix and match roof, valance and wall panels in whichever combination suits your gazebo.',
      image: '/home/gazebos/roof-panel.jpg',
      specs: [
        { label: 'Roof panels', value: '500 x 500mm or 1000 x 500mm, one to four panels per gazebo.' },
        { label: 'Valance panels', value: '1.5m or 3m wide sections, fitted to one or more sides.' },
        { label: 'Wall panel', value: 'A single branded panel to match your roof and valance choice.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Direct to Film (DTF)',
      text: 'Full-colour artwork is printed onto a film, then heat-pressed onto the canopy. Handles multiple colours, gradients, photos and fine detail, with no set-up fee or minimum order.',
    },
    {
      title: 'Heat Transfer Vinyl (HTV)',
      text: 'Your design is cut from coloured vinyl rather than printed, giving sharp edges and vibrant, long-lasting colour. Best suited to simple logos and lettering, with speciality finishes like glitter and holographic.',
    },
  ],

  gallery: [
    '/home/gazebos/barkavenue-gazebo.jpg',
    '/home/gazebos/curry-king-gazebo.jpg',
    '/home/gazebos/wildjac-gazebo.jpg',
    '/home/gazebos/mayfield-gazebo.jpg',
    '/home/gazebos/nightstop-gazebo.jpg',
    '/home/gazebos/copper-beech.jpg',
    '/home/gazebos/curry-king-gazebo-2.jpg',
  ],

  steps: [
    {
      title: 'Choose your canvas',
      text: 'Drop off your own gazebo, or select one of our tried and tested Mid-Range or Heavy-Duty frames.',
    },
    {
      title: 'Get a free quote and mockup',
      text: 'Send us your logo and our design team will put together a visual of how your gazebo could look.',
    },
    {
      title: 'Approve your order',
      text: 'Once you approve the visual we go into production, which usually takes 5 to 10 working days.',
    },
    {
      title: 'Order up',
      text: 'We will let you know when it is ready to collect from our shop in Kidderminster, or arrange delivery.',
    },
  ],

  info: [
    {
      title: 'Warranty',
      text: 'Frames and connectors carry a manufacturer warranty against defects.',
      bullets: ['Mid-Range: 1 year', 'Heavy-Duty: 2 years'],
    },
    {
      title: 'Spare parts',
      text: 'If any part takes a knock, replacement parts are available so you are not buying a whole new gazebo.',
      bullets: ['Foot plates', 'Frame brackets', 'Full legs'],
    },
    {
      title: 'Turnaround',
      text: 'Production usually takes 5 to 10 working days from approval. Got a deadline? Talk to us and we will do our best.',
    },
  ],

  cta: {
    title: 'Get your free gazebo quote and mockup.',
    text: 'Tell us about your event and we will recommend the right frame, canopy and customisation.',
    image: '/home/gazebos/gazebo-header.jpg',
  },
};

export default gazebos;
